import 'animate.css';
import FacebookPixel from 'components/FacebookPixel';
import Loader from 'components/Loader';
import {
  DATADOG_APP_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_RUM_DEPLOYMENT_VERSION,
  DATADOG_RUM_ENV,
  DATADOG_RUM_SERVICE,
  isMockingEnvEnabled,
} from 'constant';
import useGoogleFonts from 'hooks/useGoogleFonts';
import trackingShortUrl from 'lib/shorturlTracking';
import { get } from 'lodash';
import { appWithTranslation } from 'next-i18next';
import { i18n } from 'next-i18next.config';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import NProgress from 'nprogress';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';
import 'react-phone-input-2/lib/style.css';
import '../i18n';
import * as ga from '../lib/ga';
import '../styles/App.less';
import '../styles/main.css';
import { TRACKING } from '../constant/analytics';

NProgress.configure({
  minimum: 0.3,
  easing: 'ease',
  speed: 800,
  showSpinner: false,
});

Router.events.on('routeChangeStart', () => {
  NProgress.start();
});
Router.events.on('routeChangeComplete', (url) => {
  ga.event({ action: 'pageview', label: url });
  return NProgress.done();
});
Router.events.on('routeChangeError', () => NProgress.done());

function MyApp({ Component, pageProps }: AppProps) {
  const [mswState, setMswState] = useState<'unused' | 'loading' | 'ready'>('unused');

  useEffect(() => {
    console.log(`[MSW ${process.env.NEXT_PUBLIC_IA_ENV}] Current MSW State: ${mswState}`);
  }, [mswState]);

  useEffect(() => {
    ga.event({ action: 'pageview', label: window?.location?.href });
    trackingShortUrl();
  }, []);

  // Track session start immediately
  useEffect(() => {
    ga.event({
      ...(window?.__NEXT_DATA__?.page?.includes('forms') ? TRACKING.FORM.SESSION_START : TRACKING.SURVEY.SESSION_START),
      value: JSON.stringify({
        surveyId: window?.__NEXT_DATA__?.props?.pageProps?.data?.surveyData?.id ?? null,
        revisionId: window?.__NEXT_DATA__?.props?.pageProps?.data?.surveyData?.revisionId ?? null,
        page: 0, // we have to hardcode the page to 0 because infra needs to track the page for survey
      }),
    });
  }, []);

  const fontsList = [
    get(pageProps, 'data.micrositeConfigDetails.micrositeTheme.fontType'),
    get(pageProps, 'data.micrositeConfigDetails.micrositeTheme.titleFontType'),
  ];

  useGoogleFonts(fontsList);

  // MSW requires initialization of its mock server inside the server code so it can intercept requests being
  // made by Next in getServerSideProps. Unfortunately it cannot be initialized from Cypress since it's
  // running in a separate process.
  if (isMockingEnvEnabled && mswState === 'unused') {
    setMswState('loading');
    import('../mocks/index')
      .then(({ initMocks }) => initMocks())
      .then(() => setMswState('ready'))
      .catch((error) => {
        console.error('MSW initialization failed:', error);
        setMswState('ready'); // Ensure state change regardless of errors
      });
  }

  if (mswState === 'loading') {
    // If MSW is enabled we must wait for it to start up before rendering to make sure all requests
    // are captured for consistency
    return <Loader />;
  }

  return (
    <FacebookPixel>
      <>
        <Head>
          {/* maximum-scale=1 stops iOS Safari from auto-zooming when a sub-16px input is
              focused (incl. the embedded chat's message box, which lives in a cross-origin
              iframe we can't restyle). Trade-off: disables pinch-zoom across customer-pages.

              interactive-widget=resizes-content makes Android Chrome shrink the LAYOUT viewport
              for the soft keyboard (its default only shrinks the visual one), which is what lets
              the survey shell's 100dvh actually track the keyboard instead of keeping its lower
              half behind it. iOS Safari resizes the small viewport on its own. See
              momoshub/Elements#639. */}
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, interactive-widget=resizes-content"
          />
        </Head>
        <Component {...pageProps} />
      </>
    </FacebookPixel>
  );
}

// RUM session tracking is enabled for all survey and inquiry-form pages. Previously the
// form path was gated by a per-brand DATADOG_FORMS_ALLOWLIST while we were debugging the
// "location validation missed during survey submission" regression; that's resolved so
// the allowlist is removed and we use the survey-style config (20% session replay,
// `mask-user-input` privacy) consistently across both surface areas.
const isSurveyOrForm =
  typeof window === 'undefined' ||
  (typeof window !== 'undefined' &&
    /^https:\/\/.*\.?restaurant\.momos\.io\/.*\/(survey|forms)\/.*$/.test(window.location.href));

if (!isMockingEnvEnabled && isSurveyOrForm) {
  datadogRum.init({
    applicationId: DATADOG_APP_ID || '',
    clientToken: DATADOG_CLIENT_TOKEN || '',
    site: 'datadoghq.com',
    service: DATADOG_RUM_SERVICE,
    env: DATADOG_RUM_ENV,
    version: DATADOG_RUM_DEPLOYMENT_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    // Tracing configuration (disabled since we dont have APM integration at the moment)
    // traceSampleRate: 20,
    // traceContextInjection: 'sampled',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
}

export default appWithTranslation(MyApp, { i18n });
